import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faChevronRight, faListCheck, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { TransactionService } from '../../../../core/services/TransactionService';
import { ReasonCode } from '../../models/reason-code';
import { ReasonCodeEditComponent } from './reason-code-edit/reason-code-edit.component';

const MOBILE_PAGE_SIZE = 25;

@Component({
  selector: 'reason-codes',
  templateUrl: './reason-codes.component.html',
  styleUrls: ['./reason-codes.component.scss']
})
export class ReasonCodesComponent implements OnInit {
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faChevronRight = faChevronRight;
  public faListCheck = faListCheck;
  public faPlus = faPlus;
  public faSearch = faSearch;

  public isLoading = true;
  public searchTerm = '';
  public reasonCodes: ReasonCode[] = [];
  private allReasonCodes: ReasonCode[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  private destroyRef = inject(DestroyRef);

  constructor(
    private dialog: MatDialog,
    private errorService: ErrorService,
    private transactionService: TransactionService
  ) { }

  ngOnInit() {
    this.columns = [
      { field: 'reason', headerText: 'Reason Code', clipMode: 'EllipsisWithTooltip' },
      { headerText: '', template: this.actionTemplate, width: 56, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];
    this.getData();
  }

  getData() {
    this.isLoading = true;
    this.transactionService.getTransactionReasonCodes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: codes => {
        this.allReasonCodes = codes ?? [];
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();
    this.reasonCodes = term
      ? this.allReasonCodes.filter(code => (code.reason ?? '').toLowerCase().includes(term))
      : [...this.allReasonCodes];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  onRowSelected(args: RowSelectEventArgs) {
    const code = args?.data as ReasonCode;
    if (code) this.openEdit(code);
  }

  openEdit(reasonCode?: ReasonCode) {
    ReasonCodeEditComponent.open(this.dialog, { reasonCode })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(saved => {
        this.grid?.clearSelection();
        if (saved)
          this.getData();
      });
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByCode(_: number, code: ReasonCode): number | undefined {
    return code.id;
  }
}
